import { provideHttpClient, withInterceptors } from "@angular/common/http";
import type { ApplicationConfig } from "@angular/core";
import {
    ErrorHandler,
    importProvidersFrom,
    inject,
    isDevMode,
    LOCALE_ID,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZonelessChangeDetection
} from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { Auth, getAuth, provideAuth } from "@angular/fire/auth";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import {
    provideRouter,
    Router,
    withComponentInputBinding,
    withHashLocation,
    withViewTransitions
} from "@angular/router";
import { provideTransloco, TranslocoService } from "@jsverse/transloco";
import { provideHotToastConfig } from "@ngxpert/hot-toast";
import { provideNiceComponents } from "@recursyve/ngx-material-components/common";
import { provideFormFieldError } from "@recursyve/ngx-material-components/form-field-error";
import { provideAsyncTypeaheadResources } from "@recursyve/ngx-material-components/typeahead";
import { provideAdvancedFilters } from "@recursyve/nice-filter-view/advanced-filters";
import { provideNiceFilterViewTranslater } from "@recursyve/nice-filter-view/common";
import * as Sentry from "@sentry/angular";
import { FALLBACK, GravatarModule } from "ngx-gravatar";
import { provideNgxMask } from "ngx-mask";
import { EMPTY } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { environment } from "../environments/environment";
import { authInterceptor } from "./api/interceptors/auth.interceptor";
import { errorInterceptor } from "./api/interceptors/error.interceptor";
import { languageInterceptor } from "./api/interceptors/language.interceptor";
import { provideApiConfiguration } from "./api/local-144/api-configuration";
import { ApiService } from "./api/local-144/api.service";
import { getTenantContext } from "./api/local-144/fn/tenants/get-tenant-context";
import { routes } from "./app.routes";
import { LanguagePreloadService } from "./common/i18n/language-preload.service";
import { TranslocoHttpLoader } from "./common/i18n/transloco.http-loader";
import {
    applyThemeVarsToRoot,
    brandThemeVars,
    paletteToThemeVars,
    primaryThemeVars
} from "./common/theme/apply-theme-to-root";
import { tonesFromBrand } from "./common/theme/tones-from-brand";
import { AccountsTypeaheadResources } from "./features/dashboard/accounts/providers/accounts.typeahead-resource";
import { EmploymentSkillsTypeaheadResource } from "./features/dashboard/employment-skills/providers/employment-skills.typeahead-resource";
import { EmploymentSubSkillsTypeaheadResource } from "./features/dashboard/employment-sub-skills/providers/employment-sub-skills.typeahead-resource";
import { GlobalErrorHandler } from "./handlers/global-error.handler";
import { AdvancedFilterIconsService } from "./providers/advanced-filter-icons.service";
import { I18nService } from "./providers/i18n.service";
import { AppStore } from "./store/app.store";

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(GravatarModule.forRoot({ fallback: FALLBACK.mm })),
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        // eslint-disable-next-line @angular-eslint/no-developer-preview
        provideRouter(routes, withComponentInputBinding(), withHashLocation(), withViewTransitions()),
        provideHttpClient(withInterceptors([errorInterceptor, authInterceptor, languageInterceptor])),
        provideTransloco({
            config: {
                availableLangs: environment.availableLanguages,
                defaultLang: environment.defaultLanguage,
                reRenderOnLangChange: false,
                prodMode: !isDevMode()
            },
            loader: TranslocoHttpLoader
        }),
        provideApiConfiguration(environment.apiUrl),
        provideAsyncTypeaheadResources([
            AccountsTypeaheadResources,
            EmploymentSkillsTypeaheadResource,
            EmploymentSubSkillsTypeaheadResource
        ]),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth()),
        provideAppInitializer(() => {
            const auth = inject(Auth);
            const appStore = inject(AppStore);
            return inject(ApiService)
                .invoke(getTenantContext)
                .pipe(
                    tap((tenantContext) => {
                        auth.tenantId = tenantContext.firebaseId;

                        appStore.setTenantBranding({
                            name: tenantContext.name,
                            logoUrl: tenantContext.logoUrl,
                            faviconUrl: tenantContext.faviconUrl
                        });

                        appStore.setPrivacyPolicyHtml(tenantContext.privacyPolicyHtml);

                        // Il ne faut pas appliquer les variables CSS dans le store,
                        // car nous ne devons pas réagir au changement de thème
                        // dans un effect pour éviter les flash of unstyled content.
                        //
                        // Seule la palette primary varie d'un tenant à l'autre : elle est générée
                        // à partir de brandColor. Les six autres sont identiques partout et déjà
                        // compilées dans m3-theme.scss, donc il n'y a rien à surcharger pour
                        // elles. On retombe sur le blob de l'API seulement pour un tenant sans
                        // couleur de marque.
                        const brandColor = tenantContext.brandColor;
                        const primary =
                            brandColor === null
                                ? (tenantContext.themeVariables["primary"] ?? {})
                                : tonesFromBrand(brandColor);

                        applyThemeVarsToRoot({
                            ...(brandColor === null
                                ? paletteToThemeVars(tenantContext.themeVariables)
                                : primaryThemeVars(brandColor)),
                            ...brandThemeVars(primary, brandColor)
                        });
                    }),
                    catchError(() => EMPTY)
                );
        }),
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        {
            provide: Sentry.TraceService,
            deps: [Router]
        },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        provideAnimationsAsync(),
        provideHotToastConfig({ dismissible: true, position: "bottom-right" }),
        provideMomentDateAdapter(),
        provideNgxMask(),
        {
            provide: LOCALE_ID,
            useFactory: () => {
                const lang = localStorage.getItem("lang") ?? environment.defaultLanguage;
                return lang === "en" ? "en-US" : "fr-CA";
            }
        },
        {
            provide: MatPaginatorIntl,
            useFactory: (i18nService: I18nService) => i18nService.getPaginatorIntl(),
            deps: [I18nService]
        },
        provideAppInitializer(() => inject(LanguagePreloadService).preload()),
        provideAdvancedFilters({ iconsResolver: AdvancedFilterIconsService }),
        provideFormFieldError({
            translater: {
                deps: [TranslocoService],
                useFactory: (service: TranslocoService) => (key, params) => service.translate(key, params)
            }
        }),
        provideNiceComponents({
            translater: {
                deps: [TranslocoService],
                useFactory: (service: TranslocoService) => (key, params) => service.translate(key, params)
            }
        }),
        provideNiceFilterViewTranslater({
            translater: {
                deps: [TranslocoService],
                useFactory: (service: TranslocoService) => (key, params) => service.translate(key, params)
            }
        })
    ]
};
